<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';
import { useDisplay } from 'vuetify';
import sidebarItems from '@/components/layout/full/vertical-sidebar/sidebarItem';
import { pathAllowed } from '@/utils/menuAcl';
import { anyChildActive } from '@/utils/navActive';
import type { menu } from '@/utils/sidebarMenu';

const reconCount = ref(0);
const qualityGap = ref(0);
const badgeState = ref({
  reconciliation: 0,
  reconColor: 'error',
  qualityGaps: 0,
  accountsIssues: 0,
  pendingKasbon: 0,
  kasbonRecords: 0,
  attendanceAttention: 0,
  portalPending: 0,
  reminderNotifications: 0,
});

const {
  draft,
  isDirty,
  load: loadFavs,
  save: saveFavs,
  discard: discardFavs,
  add: addFav,
  remove: removeFav,
  isInDraft,
} = useFavoriteMenus();
const { t, load: loadLocale } = useI18n();
const { load: loadTheme } = useThemeMode();
const { me, refresh: refreshAuth, filterMenuTree } = useAuthSession();
const { canBack, canNext, goBack, goNext } = useNavHistory();
const route = useRoute();
/** Beranda disembunyikan saat sudah di halaman utama */
const isHome = computed(() => {
  const p = String(route.path || '/').replace(/\/+$/, '') || '/';
  return p === '/' || p === '';
});
let pollTimer: number | undefined;

const baseMenu = shallowRef(sidebarItems.map((item) => ({ ...item })));

function flattenMenuItems(items: typeof sidebarItems): typeof sidebarItems {
  const out: typeof sidebarItems = [];
  for (const item of items) {
    if (item.to) out.push(item);
    if (item.children?.length) out.push(...flattenMenuItems(item.children));
  }
  return out;
}

function mapMenuDeep(
  items: typeof sidebarItems,
  patch: (item: (typeof sidebarItems)[number]) => (typeof sidebarItems)[number]
): typeof sidebarItems {
  return items.map((item) => {
    const copy = patch({ ...item });
    if (item.children?.length) {
      copy.children = mapMenuDeep(item.children, patch);
    }
    return copy;
  });
}

function chipOf(n: number, color: string) {
  return {
    chip: n > 0 ? String(n) : '',
    chipColor: n > 0 ? color : '',
  };
}

function rebuildBase(state: Partial<typeof badgeState.value> = {}) {
  const next = { ...badgeState.value, ...state };
  badgeState.value = next;
  reconCount.value = next.reconciliation;
  qualityGap.value = next.qualityGaps;

  baseMenu.value = mapMenuDeep(sidebarItems, (item) => {
    const copy = { ...item };
    if (item.to === '/reconciliation') {
      Object.assign(copy, chipOf(next.reconciliation, next.reconColor || 'error'), {
        chipTitle:
          next.reconciliation > 0
            ? t('nav.reconPendingTip', { n: next.reconciliation })
            : '',
      });
    } else if (item.to === '/control/quality') {
      Object.assign(copy, chipOf(next.qualityGaps, 'warning'));
    } else if (item.to === '/accounts') {
      Object.assign(copy, chipOf(next.accountsIssues, 'warning'));
    } else if (item.to === '/hr/employees') {
      Object.assign(copy, chipOf(next.pendingKasbon, 'error'));
    } else if (item.to === '/kasbon') {
      Object.assign(copy, chipOf(next.kasbonRecords, 'warning'));
    } else if (item.to === '/hr/attendance') {
      Object.assign(copy, chipOf(next.attendanceAttention, 'info'));
    } else if (item.to === '/hr/me') {
      Object.assign(copy, chipOf(next.portalPending, 'primary'));
    } else if (item.to === '/reminders') {
      Object.assign(copy, chipOf(next.reminderNotifications, 'error'));
    } else if (item.titleKey === 'nav.activities') {
      Object.assign(copy, chipOf(next.reminderNotifications + next.kasbonRecords, 'warning'));
    }
    return copy;
  });
}

function applyReconChip(n: number, color = 'error') {
  rebuildBase({ reconciliation: n, reconColor: color || 'error' });
}

async function refreshBadges() {
  try {
    const data = await $fetch<{
      reconciliation: number;
      reconColor?: string;
      qualityGaps: number;
      accountsIssues: number;
      pendingKasbon: number;
      kasbonRecords: number;
      attendanceAttention: number;
      portalPending: number;
      reminderNotifications: number;
    }>('/api/nav/badges');
    rebuildBase({
      reconciliation: Number(data.reconciliation) || 0,
      reconColor: data.reconColor || 'error',
      qualityGaps: Number(data.qualityGaps) || 0,
      accountsIssues: Number(data.accountsIssues) || 0,
      pendingKasbon: Number(data.pendingKasbon) || 0,
      kasbonRecords: Number(data.kasbonRecords) || 0,
      attendanceAttention: Number(data.attendanceAttention) || 0,
      portalPending: Number(data.portalPending) || 0,
      reminderNotifications: Number(data.reminderNotifications) || 0,
    });
  } catch {
    /* ignore — jangan kosongkan chip lama jika gagal singkat */
  }
}

function onReconEvent(e: Event) {
  const detail = (e as CustomEvent).detail;
  if (detail && typeof detail === 'object' && 'pendingCount' in detail) {
    applyReconChip(Number(detail.pendingCount) || 0, detail.badgeColor || 'error');
  } else if (typeof detail === 'number') {
    applyReconChip(detail);
  } else {
    refreshBadges();
  }
}

const favMenuItems = computed(() => {
  const flatStatic = flattenMenuItems(sidebarItems);
  const flatLive = flattenMenuItems(baseMenu.value);
  const menus = me.value.menus;
  return draft.value
    .map((to) => flatStatic.find((i) => i.to === to))
    .filter(Boolean)
    .filter((item) => !item!.to || !menus || pathAllowed(item!.to, menus))
    .map((item) => {
      const live = flatLive.find((i) => i.to === item!.to);
      return {
        ...item!,
        chip: live?.chip ?? item!.chip,
        chipColor: live?.chipColor ?? item!.chipColor,
      };
    });
});

const visibleSidebarMenu = computed(() => {
  return filterMenuTree(baseMenu.value);
});

/** Auto-expand groups that contain the current page */
const openedGroups = ref<string[]>([]);

function collectOpenGroups(items: menu[], path: string, acc: string[] = []) {
  for (const item of items) {
    if (item.children?.length) {
      const key = item.titleKey || item.title || '';
      if (key && anyChildActive(item.children, path)) acc.push(key);
      collectOpenGroups(item.children, path, acc);
    }
  }
  return acc;
}

watch(
  () => [route.path, visibleSidebarMenu.value] as const,
  ([path, menu]) => {
    const next = collectOpenGroups(menu as menu[], String(path));
    const merged = new Set([...openedGroups.value, ...next]);
    openedGroups.value = [...merged];
  },
  { immediate: true }
);

function onAddFav(to: string) {
  addFav(to);
}

function onRemoveFav(to: string) {
  removeFav(to);
}

function onSaveFavs() {
  saveFavs();
  favEditMode.value = false;
}

function onCancelFavs() {
  discardFavs();
  favEditMode.value = false;
}

/** Mode kelola pin — Add/X hanya muncul setelah tombol Pin ditekan */
const favEditMode = ref(false);

function toggleFavEdit() {
  if (favEditMode.value) {
    // Keluar mode tanpa simpan → buang draft
    if (isDirty.value) discardFavs();
    favEditMode.value = false;
    return;
  }
  favEditMode.value = true;
}

/** Sidebar default tertutup — buka lewat tombol menu */
const sDrawer = ref(false);
const router = useRouter();
const { mode: deviceMode, load: loadDevice, CHANGE_EVENT } = useDevicePreview();
const display = useDisplay();

/** Header rapat: HP preview atau layar sempit */
const compactHeader = computed(
  () => deviceMode.value === 'hp' || display.width.value < 720
);
/** HP: notch + jam 2 baris (tgl atas, sesi bawah) */
const headerHeight = computed(() => {
  if (deviceMode.value === 'hp') return 76;
  if (compactHeader.value) return 88;
  return 64;
});

function goHome() {
  sDrawer.value = false;
  router.push('/');
}

function onDevicePreviewChange() {
  // Tutup drawer biar tidak bocor di tepi frame HP
  sDrawer.value = false;
}

onMounted(() => {
  loadLocale();
  loadTheme();
  loadDevice();
  loadFavs();
  void refreshAuth();
  window.addEventListener('jgi-auth-changed', () => {
    void refreshAuth();
  });
  window.addEventListener(CHANGE_EVENT, onDevicePreviewChange);
  rebuildBase();
  refreshBadges();
  window.addEventListener('recon-count', onReconEvent);
  window.addEventListener('jgi-nav-badges-refresh', refreshBadges);
  pollTimer = window.setInterval(refreshBadges, 60000);
});
onUnmounted(() => {
  if (pollTimer) window.clearInterval(pollTimer);
  window.removeEventListener('recon-count', onReconEvent);
  window.removeEventListener('jgi-nav-badges-refresh', refreshBadges);
  window.removeEventListener(CHANGE_EVENT, onDevicePreviewChange);
});
</script>

<template>
  <v-navigation-drawer
    left
    elevation="0"
    temporary
    :absolute="deviceMode !== 'desktop'"
    :disable-resize-watcher="deviceMode !== 'desktop'"
    :width="deviceMode === 'hp' ? 304 : compactHeader ? 260 : 280"
    class="leftSidebar"
    :class="{
      'leftSidebar--framed': deviceMode !== 'desktop',
      'leftSidebar--hp': deviceMode === 'hp',
    }"
    v-model="sDrawer"
  >
    <div class="sidebar-brand">
      <div class="sidebar-brand__logo">
        <LayoutFullLogo />
      </div>
      <div class="sidebar-brand__actions">
        <button
          type="button"
          class="sidebar-icon-btn sidebar-icon-btn--home"
          :title="t('common.home')"
          :aria-label="t('common.home')"
          @click="goHome"
        >
          <v-icon size="18">mdi-home</v-icon>
        </button>
        <button
          type="button"
          class="sidebar-icon-btn sidebar-icon-btn--close"
          :title="t('common.close')"
          :aria-label="t('common.close')"
          @click="sDrawer = false"
        >
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
    </div>

    <div class="sidebar-nav">
      <perfect-scrollbar class="scrollnavbar">
        <!-- FAVORIT -->
        <div class="sidebar-fav" :class="{ 'sidebar-fav--edit': favEditMode }">
          <div class="sidebar-fav__head">
            <div class="sidebar-fav__label">{{ t('fav.title') }}</div>
            <button
              type="button"
              class="sidebar-fav__pin-btn"
              :class="{ 'sidebar-fav__pin-btn--on': favEditMode }"
              :title="favEditMode ? t('fav.done') : t('fav.manage')"
              :aria-label="favEditMode ? t('fav.done') : t('fav.manage')"
              @click="toggleFavEdit"
            >
              <v-icon size="16">{{ favEditMode ? 'mdi-pin' : 'mdi-pin-outline' }}</v-icon>
            </button>
          </div>

          <div v-if="!favMenuItems.length" class="sidebar-fav__empty">
            {{ favEditMode ? t('fav.emptyEdit') : t('fav.empty') }}
          </div>

          <v-list v-else class="sidebar-list sidebar-fav__list pa-0" nav>
            <LayoutFullVerticalSidebarNavItem
              v-for="item in favMenuItems"
              :key="'fav-' + item.to"
              :item="item"
              :fav-action="favEditMode ? 'remove' : null"
              class="leftPadding"
              @remove="onRemoveFav"
            />
          </v-list>

          <div v-if="favEditMode" class="sidebar-fav__actions">
            <button
              type="button"
              class="sidebar-fav__btn sidebar-fav__btn--cancel"
              @click="onCancelFavs"
            >
              {{ t('fav.cancel') }}
            </button>
            <button
              type="button"
              class="sidebar-fav__btn sidebar-fav__btn--save"
              :disabled="!isDirty"
              @click="onSaveFavs"
            >
              <v-icon size="16" class="sidebar-fav__btn-ico">mdi-content-save-outline</v-icon>
              {{ t('fav.save') }}
            </button>
          </div>
          <div v-else-if="isDirty" class="sidebar-fav__hint">
            {{ t('fav.dirtyHint') }}
          </div>
        </div>

        <LayoutReconSidebarWidget />

        <!-- MENU UTAMA -->
        <v-list v-model:opened="openedGroups" class="sidebar-list pa-3 pt-1" nav>
          <template v-for="(item, i) in visibleSidebarMenu">
            <LayoutFullVerticalSidebarNavGroup
              v-if="item.header"
              :key="'h-' + item.header + i"
              :item="item"
            />
            <LayoutFullVerticalSidebarNavCollapse
              v-else-if="item.children"
              :key="'c-' + item.title"
              class="leftPadding"
              :item="item"
              :level="0"
            />
            <LayoutFullVerticalSidebarNavItem
              v-else
              :key="'i-' + (item.to || item.title) + i"
              :item="item"
              :fav-action="favEditMode && item.to && !isInDraft(item.to) ? 'add' : null"
              class="leftPadding"
              @add="onAddFav"
            />
          </template>
        </v-list>
      </perfect-scrollbar>
    </div>
  </v-navigation-drawer>

  <v-app-bar
    elevation="0"
    :height="headerHeight"
    class="top-header"
    :class="{
      'top-header--compact': compactHeader,
      'top-header--hp': deviceMode === 'hp',
    }"
    app
  >
    <div class="header-bar w-100 px-2" :class="{ 'header-bar--compact': compactHeader }">
      <div class="d-flex align-center header-left">
        <nav v-if="!sDrawer" class="hdr-nav" :aria-label="t('common.menu')">
          <button
            type="button"
            class="hdr-nav__item"
            :title="t('common.menu')"
            :aria-label="t('common.menu')"
            @click="sDrawer = true"
          >
            <v-icon size="18">mdi-menu</v-icon>
            <span v-if="!compactHeader" class="hdr-nav__txt d-none d-sm-inline">{{ t('common.menu') }}</span>
          </button>

          <template v-if="!isHome">
            <span class="hdr-nav__sep" aria-hidden="true" />
            <NuxtLink
              class="hdr-nav__item"
              to="/"
              :title="t('common.home')"
              :aria-label="t('common.home')"
            >
              <v-icon size="18">mdi-home-outline</v-icon>
              <span v-if="!compactHeader" class="hdr-nav__txt d-none d-sm-inline">{{ t('common.home') }}</span>
            </NuxtLink>
          </template>

          <template v-if="canBack || canNext">
            <span class="hdr-nav__sep" aria-hidden="true" />
            <button
              v-if="canBack"
              type="button"
              class="hdr-nav__item hdr-nav__item--icon"
              :title="t('common.backHint')"
              :aria-label="t('common.back')"
              @click="goBack"
            >
              <v-icon size="20">mdi-arrow-left</v-icon>
            </button>
            <button
              v-if="canNext"
              type="button"
              class="hdr-nav__item hdr-nav__item--icon"
              :title="t('common.nextHint')"
              :aria-label="t('common.next')"
              @click="goNext"
            >
              <v-icon size="20">mdi-arrow-right</v-icon>
            </button>
          </template>
        </nav>
      </div>

      <div class="header-center">
        <LayoutFullVerticalHeaderSessionClock
          variant="header"
          :compact="compactHeader"
        />
      </div>

      <div class="d-flex align-center header-right ga-0">
        <LayoutFullVerticalHeaderPrivacyEye />
        <LayoutFullVerticalHeaderNotificationDD />
        <LayoutFullVerticalHeaderProfileDD />
      </div>
    </div>
  </v-app-bar>
</template>

<style scoped>
.header-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
  min-height: 40px;
  width: 100%;
}
.header-bar--compact {
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  row-gap: 2px;
  column-gap: 4px;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}
.header-bar--compact .header-left {
  grid-column: 1;
  grid-row: 1;
}
.header-bar--compact .header-right {
  grid-column: 2;
  grid-row: 1;
  justify-self: end;
}
.header-bar--compact .header-center {
  grid-column: 1 / -1;
  grid-row: 2;
  justify-self: center;
  width: 100%;
  min-width: 0;
  padding: 0 2px;
}

/* HP: satu baris — sisi kiri/kanan setimbang supaya jam di tengah geometris */
.top-header--hp .header-bar,
.top-header--hp .header-bar--compact {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) !important;
  grid-template-rows: auto !important;
  align-items: center !important;
  column-gap: 2px !important;
  row-gap: 0 !important;
  padding: 0 2px !important;
  min-height: 40px !important;
  width: 100%;
}
.top-header--hp .header-bar--compact .header-left {
  grid-column: 1 !important;
  grid-row: 1 !important;
  justify-self: start !important;
}
.top-header--hp .header-bar--compact .header-center {
  grid-column: 2 !important;
  grid-row: 1 !important;
  justify-self: center !important;
  width: auto !important;
  max-width: 100%;
  min-width: 0;
  padding: 0 !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}
.top-header--hp .header-bar--compact .header-right {
  grid-column: 3 !important;
  grid-row: 1 !important;
  justify-self: end !important;
}
.top-header--hp .header-left,
.top-header--hp .header-right {
  min-height: 32px;
  height: 32px;
  display: inline-flex !important;
  align-items: center !important;
  gap: 2px !important;
}
.top-header--hp .header-left .v-btn,
.top-header--hp .header-right .v-btn {
  width: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  margin: 0 !important;
}
.top-header--hp .header-left .v-btn :deep(.v-icon),
.top-header--hp .header-right .v-btn :deep(.v-icon),
.top-header--hp .header-left .v-btn :deep(svg),
.top-header--hp .header-right .v-btn :deep(svg) {
  width: 17px !important;
  height: 17px !important;
  font-size: 17px !important;
}
.top-header--hp .header-right :deep(.v-avatar),
.top-header--hp .header-right :deep(.profile-dd__activator) {
  width: 28px !important;
  height: 28px !important;
}
.top-header--hp .header-right :deep(.v-avatar img),
.top-header--hp .header-right :deep(.profile-dd__activator img) {
  width: 28px !important;
  height: 28px !important;
}
.top-header--hp .header-right :deep(.me-1) {
  margin-inline-end: 0 !important;
}
.top-header--hp .header-right :deep(.v-badge) {
  margin: 0 !important;
}
.top-header--hp .header-right :deep(.v-badge__badge) {
  font-size: 9px !important;
  height: 14px !important;
  min-width: 14px !important;
  padding: 0 3px !important;
}
.top-header--hp :deep(.session-clock--header.session-clock--compact) {
  width: auto;
  max-width: 120px;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
  gap: 1px !important;
  padding: 0 !important;
}
.top-header--hp :deep(.session-clock__date),
.top-header--hp :deep(.session-clock__sesi) {
  display: block;
  width: 100%;
  text-align: center;
}
.header-left {
  min-height: 36px;
  min-width: 0;
  justify-self: start;
}
.header-center {
  justify-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  max-width: 100%;
  pointer-events: none;
}
.header-right {
  justify-self: end;
  min-width: 0;
  flex-shrink: 0;
}
.top-header--compact {
  overflow: visible !important;
}
.hdr-nav {
  --hdr-nav-fg: #334155;
  --hdr-nav-muted: #64748b;
  --hdr-nav-bg: #ffffff;
  --hdr-nav-border: rgba(15, 23, 42, 0.08);
  --hdr-nav-hover: rgba(15, 23, 42, 0.05);
  --hdr-nav-active: rgba(79, 70, 229, 0.1);
  --hdr-nav-accent: #4f46e5;
  --hdr-nav-sep: rgba(15, 23, 42, 0.1);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  height: 38px;
  padding: 3px;
  border-radius: 12px;
  background: var(--hdr-nav-bg);
  border: 1px solid var(--hdr-nav-border);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
}
.hdr-nav__sep {
  width: 1px;
  height: 18px;
  margin: 0 3px;
  background: var(--hdr-nav-sep);
  border-radius: 1px;
  flex-shrink: 0;
}
.hdr-nav__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  min-width: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--hdr-nav-fg);
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
  font: inherit;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease;
}
.hdr-nav__item--icon {
  padding: 0;
  width: 30px;
  color: var(--hdr-nav-muted);
}
.hdr-nav__item :deep(.v-icon),
.hdr-nav__item :deep(svg) {
  color: inherit !important;
}
.hdr-nav__txt {
  font-size: 12.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.hdr-nav__item:hover {
  background: var(--hdr-nav-hover);
  color: #0f172a;
}
.hdr-nav__item:active {
  background: var(--hdr-nav-active);
  color: var(--hdr-nav-accent);
  transform: scale(0.97);
}
.hdr-nav__item:focus-visible {
  outline: 2px solid var(--hdr-nav-accent);
  outline-offset: 1px;
}

:global(html.jgi-dark) .hdr-nav {
  --hdr-nav-fg: #e2e8f0;
  --hdr-nav-muted: #94a3b8;
  --hdr-nav-bg: rgba(30, 41, 59, 0.92);
  --hdr-nav-border: rgba(255, 255, 255, 0.1);
  --hdr-nav-hover: rgba(255, 255, 255, 0.08);
  --hdr-nav-active: rgba(129, 140, 248, 0.18);
  --hdr-nav-accent: #a5b4fc;
  --hdr-nav-sep: rgba(255, 255, 255, 0.12);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.03) inset;
}
:global(html.jgi-dark) .hdr-nav__item:hover {
  color: #f8fafc;
}

.top-header--hp .hdr-nav {
  height: 34px;
  padding: 2px;
  border-radius: 10px;
  gap: 1px;
}
.top-header--hp .hdr-nav__item {
  height: 28px;
  min-width: 28px;
  padding: 0 8px;
  border-radius: 8px;
}
.top-header--hp .hdr-nav__item--icon {
  width: 28px;
  padding: 0;
}
.top-header--hp .hdr-nav__sep {
  height: 14px;
  margin: 0 2px;
}
.sidebar-brand__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.leftSidebar--hp .sidebar-brand__actions {
  gap: 6px;
}
.leftSidebar--hp .sidebar-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 9px;
}
.leftSidebar--hp .sidebar-fav {
  margin: 8px 10px 8px;
  padding: 8px 8px 6px;
  border-radius: 11px;
}
.leftSidebar--hp .sidebar-fav__empty {
  font-size: 11px;
  padding: 6px 2px 4px;
}
.leftSidebar--hp .sidebar-list.pa-3 {
  padding: 4px 10px 12px !important;
}
.sidebar-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    filter 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;
}
.sidebar-icon-btn--home {
  color: #0f766e;
  background: #f0fdfa;
  border-color: #99f6e4;
}
.sidebar-icon-btn--home:hover,
.sidebar-icon-btn--home:active {
  transform: scale(1.05);
  background: #ccfbf1;
  color: #115e59;
}
.sidebar-icon-btn--close {
  color: #fff;
  background: linear-gradient(135deg, #ef4444 0%, #be123c 100%);
  border-color: #be123c;
  box-shadow: 0 6px 14px -6px rgba(190, 18, 60, 0.75);
}
.sidebar-icon-btn--close:hover,
.sidebar-icon-btn--close:active {
  filter: brightness(1.08);
  transform: scale(1.05);
}
.sidebar-fav {
  margin: 6px 12px 10px;
  padding: 10px 10px 8px;
  border-radius: 12px;
  border: 1px solid rgba(15, 118, 110, 0.14);
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 14px -10px rgba(15, 118, 110, 0.28);
}
.sidebar-fav--edit {
  border-color: #5eead4;
  background: #f0fdfa;
  box-shadow: 0 0 0 1px rgba(15, 118, 110, 0.1);
}
.sidebar-fav__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 28px;
  margin-bottom: 4px;
}
.sidebar-fav__label {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0f766e;
  line-height: 1;
}
.sidebar-fav--edit .sidebar-fav__label {
  color: #115e59;
}
.sidebar-fav__pin-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(15, 118, 110, 0.14);
  border-radius: 8px;
  cursor: pointer;
  color: #5b6b76;
  background: rgba(255, 255, 255, 0.7);
  padding: 0;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.12s ease;
}
.sidebar-fav__pin-btn:hover,
.sidebar-fav__pin-btn:active {
  color: #0f766e;
  border-color: #5eead4;
  background: #ccfbf1;
  transform: scale(1.04);
}
.sidebar-fav__pin-btn--on {
  color: #fff;
  background: linear-gradient(135deg, #0f766e, #0369a1);
  border-color: #0f766e;
  box-shadow: 0 4px 10px -6px rgba(15, 118, 110, 0.55);
}
.sidebar-fav__empty {
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.4;
  padding: 8px 4px 6px;
  text-align: center;
}
.sidebar-fav__list {
  margin: 2px 0 4px;
}
.sidebar-fav__hint {
  margin-top: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  text-align: center;
}
.sidebar-fav__actions {
  display: grid;
  grid-template-columns: 1fr 1.35fr;
  gap: 6px;
  margin-top: 8px;
}
@media (max-width: 720px) {
  .sidebar-fav__actions {
    grid-template-columns: 1fr;
  }
}
.sidebar-fav__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 34px;
  padding: 0 10px;
  border-radius: 9px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    opacity 0.15s ease,
    transform 0.12s ease;
}
.sidebar-fav__btn-ico {
  flex-shrink: 0;
}
.sidebar-fav__btn--cancel {
  color: #475569;
  background: #f1f5f9;
  border-color: #e2e8f0;
}
.sidebar-fav__btn--cancel:hover,
.sidebar-fav__btn--cancel:active {
  background: #e2e8f0;
  color: #0f172a;
}
.sidebar-fav__btn--save {
  color: #fff;
  background: linear-gradient(135deg, #0f766e, #0369a1);
  border-color: #0f766e;
  box-shadow: 0 6px 12px -8px rgba(15, 118, 110, 0.55);
}
.sidebar-fav__btn--save:hover:not(:disabled) {
  background: linear-gradient(135deg, #115e59, #0284c7);
  transform: translateY(-1px);
}
.sidebar-fav__btn--save:disabled {
  opacity: 0.4;
  cursor: default;
  box-shadow: none;
  transform: none;
}
</style>
